import React, {LegacyRef, useRef} from 'react';
import {useNavigate} from 'react-router';
import {getMyCars} from '@di-shared/api/cci/auth';
import {diApiNa} from '@di-shared/api/di';
import {DEFAULT_CACHE_TIME} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {BrandType} from '@shared/cci/features/const/environment';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useQuery} from '@tanstack/react-query';

const HEADER_HEIGHT = 60;

export default function () {
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const startPointRef = useRef<Element>(null);
  const {vin, setVin} = useEnvironmentStore(({vin, setVin}) => ({vin, setVin}));
  const navigate = useNavigate();
  const {data: myCars} = useQuery({
    queryKey: [getQueryKey(getMyCars)],
    queryFn: () => getMyCars(),
    enabled: !vin,
    ...DEFAULT_CACHE_TIME,
  });
  const targetVin = vin || myCars?.contents.find(({isMainVehicle}) => isMainVehicle)?.vin;
  const {data: vehicleInfo} = useQuery({
    queryKey: [getQueryKey(diApiNa.vehicle.getVehicleInfo), targetVin],
    queryFn: () => diApiNa.vehicle.getVehicleInfo({vin: targetVin!}),
    select: ({data}) => data,
    enabled: !!targetVin,
    ...DEFAULT_CACHE_TIME,
  });
  const hasSupportedFeatures = (vehicleInfo?.supportedFeatures.length ?? 0) > 0;

  const [goToHomePage] = usePendingHandler(
    async () => {
      if (!targetVin) {
        backOrClose();
        return;
      }
      if (vin !== targetVin) {
        setVin(targetVin);
      }
      navigate(ROUTE_PATHS.HOME.getFullPath(targetVin), {replace: true});
    },
    () => {
      backOrClose();
    }
  );

  function scrollToStart() {
    if (startPointRef.current) {
      window.scrollTo({
        top: startPointRef.current.getBoundingClientRect().y - HEADER_HEIGHT,
        behavior: 'smooth',
      });
    }
  }

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1
          className="headline_xxsmall nav_center_title"
          aria-hidden="true"
        >
          <span className="blind">{t('msg_introduction_bodytext')}</span>
        </h1>
      </Header>

      <main className="content page_home">
        <section className="section section_intro">
          {/* ① DI 소개 */}
          <section className="section intro_driving_insight">
            <div className="intro_heading">
              <span
                className="ic logo_driving_insight"
                role="img"
                aria-label="Driving Insight"
              />
              <h2
                className="intro_heading_title"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('msg_introduction_title')}
              </h2>
            </div>
            <div className="intro_motion">
              <div className="intro_imgbox">
                <ImageSelector
                  className="intro_img"
                  alt="driving insight intro view"
                  kiaLightSrc={() => import('@di-shared/assets/img/home/intro_driving_insight_eu02_light.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/home/intro_driving_insight_eu02.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/intro_driving_insight_eu02_light.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/intro_driving_insight_eu02.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/intro_driving_insight_eu02_light.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/intro_driving_insight_eu02.png')}
                />
              </div>
            </div>
            <button
              type="button"
              className="intro_start_btn"
              aria-label={t('msg_introduction_bodytext')}
              onClick={() => scrollToStart()}
            >
              {t('msg_introduction_bodytext')}
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </section>

          <div
            ref={startPointRef as LegacyRef<HTMLDivElement>}
            style={{position: 'relative', top: 140}}
          />

          {/* ② 드라이빙 습관 */}
          {import.meta.env.VITE_BRAND === BrandType.KIA && (
            <section className="section intro_my_trips">
              <div className="intro_heading">
                <h3
                  className="intro_title"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('msg_introduction_driving_habit_title')}
                </h3>
                <p
                  className="intro_desc"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('msg_introduction_driving_habit_01')}
                </p>
              </div>
              <div
                className="intro_motion"
                aria-hidden="true"
              >
                <div className="intro_imgbox">
                  <ImageSelector
                    className="intro_img"
                    alt="driving habit view"
                    kiaLightSrc={() => import('@di-shared/assets/img/home/intro_my_trips_eu02_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/home/intro_my_trips_eu02.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/intro_my_trips_eu02_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/intro_my_trips_eu02.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/intro_my_trips_eu02_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/intro_my_trips_eu02.png')}
                  />
                </div>
              </div>
            </section>
          )}

          {/* ③ 드라이빙 히스토리 */}
          <section className="section intro_driving_history">
            <div className="intro_heading">
              <h3
                className="intro_title"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('msg_introduction_driving_history_title')}
              </h3>
              <p
                className="intro_desc"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('msg_introduction_driving_history_01')}
              </p>
            </div>
            <div
              className="intro_motion"
              aria-hidden="true"
            >
              <div className="intro_imgbox">
                <ImageSelector
                  className="intro_img"
                  alt="driving history view"
                  kiaLightSrc={() => import('@di-shared/assets/img/home/intro_driving_history_eu02_light.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/home/intro_driving_history_eu02.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/intro_driving_history_eu02_light.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/intro_driving_history_eu02.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/intro_driving_history_eu02_light.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/intro_driving_history_eu02.png')}
                />
              </div>
            </div>
          </section>

          {/* ④ 데이터 알아보기 카드 */}
          <section className="section intro_data_explore">
            <div className="section_container">
              <div
                className="section_container_header"
                aria-hidden="true"
              >
                <h3 className="title_xsmall_b">{t('msg_introduction_knowing_data_title')}</h3>
                <span className="ic ic_arrow_forward_16px" />
              </div>
              <p
                className="section_container_desc"
                aria-hidden="true"
              >
                {t('msg_introduction_knowing_data_01')}
              </p>
              <div className="intro_motion">
                <div className="intro_imgbox">
                  <ImageSelector
                    className="intro_img"
                    alt="knowing data view"
                    kiaLightSrc={() => import('@di-shared/assets/img/home/intro_data_explore_eu02_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/home/intro_data_explore_eu02.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/intro_data_explore_eu02_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/intro_data_explore_eu02.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/intro_data_explore_eu02_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/intro_data_explore_eu02.png')}
                  />
                </div>
              </div>
              <LinkTo
                to={ROUTE_PATHS.ABOUT_DATA_EXPLORE.getFullPath()}
                className="section_container_full_btn"
                aria-label={`${t('msg_introduction_knowing_data_title')} ${t('msg_introduction_knowing_data_01')}`}
              />
            </div>
          </section>
        </section>

        <div className="divider divider_thick" />
        <section className="section section_notice">
          <ul className="notice_list">
            <li
              className="notice_item"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('msg_introduction_knowing_data_02')}
            </li>
            <li
              className="notice_item"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('msg_introduction_knowing_data_03')}
            </li>
          </ul>
        </section>
      </main>

      {hasSupportedFeatures && (
        <div className="floating_action_area">
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={() => {
              emitEventLog({
                event: 'di_driveinfo_interaction',
                data_action: 'start driving insight',
                data_name: t('msg_introduction_button'),
              });
              goToHomePage();
            }}
          >
            {t('msg_introduction_button')}
          </button>
        </div>
      )}
    </div>
  );
}
